import {
  BrowserAutomationDataSchema,
  BrowserAutomationInputSchema,
  BROWSER_AUTOMATION_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import { sanitizeBrowserAutomationResponseData } from '../../../../utils/browserAutomationResponse.utils';
import {
  getElectronApiUnavailableResult,
  isElectronApiAvailable,
  parsePortalToolHandlerInput,
  resolvePortalApplicationId,
} from '../portal-tool-handler.utils';
import type { PortalToolHandler } from '../types';

export const browserAutomationHandler: PortalToolHandler = async ({
  input,
  toolCallId,
  batchId,
  applicationId,
}) => {
  const parsedInput = parsePortalToolHandlerInput({
    schema: BrowserAutomationInputSchema,
    input,
    toolName: BROWSER_AUTOMATION_TOOL_NAME,
    toolCallId,
    batchId,
  });
  if (!parsedInput.ok) {
    return parsedInput.result;
  }

  if (!isElectronApiAvailable() || !window.electronAPI.browser?.automation) {
    return getElectronApiUnavailableResult({
      toolName: BROWSER_AUTOMATION_TOOL_NAME,
      toolCallId,
      batchId,
      message: 'Browser automation API is not available',
    });
  }

  const resolvedApplicationId = resolvePortalApplicationId({ applicationId });

  const result = await window.electronAPI.browser.automation({
    applicationId: resolvedApplicationId,
    instanceId: parsedInput.data.instanceId,
    pageId: parsedInput.data.pageId,
    action: parsedInput.data.action,
    url: parsedInput.data.url,
    elementIndex: parsedInput.data.elementIndex,
    text: parsedInput.data.text,
    scrollDirection: parsedInput.data.scrollDirection,
    scrollAmount: parsedInput.data.scrollAmount,
    waitMs: parsedInput.data.waitMs,
    completionMessage: parsedInput.data.completionMessage,
    script: parsedInput.data.script,
  });

  if (!result.success || !result.data) {
    return {
      status: 'error',
      result: {
        message: result.error || 'Browser automation failed',
      },
    };
  }

  const parsedData = BrowserAutomationDataSchema.safeParse({
    ...result.data,
    actionMessage:
      result.data.actionMessage ??
      `Browser automation ${parsedInput.data.action} completed`,
  });

  if (!parsedData.success) {
    return {
      status: 'error',
      result: {
        message: `Invalid browser automation response: ${parsedData.error.message}`,
      },
    };
  }

  return {
    status: 'success',
    result: {
      message: parsedData.data.actionMessage ?? 'Browser automation completed',
      data: sanitizeBrowserAutomationResponseData({ data: parsedData.data }),
    },
  };
};
